'use client';

import React, { createContext, useContext, useCallback, useState, useEffect, ReactNode } from 'react';
import type { FinancialTransaction } from '@/lib/utils/financialUtils';
import {
  createTransaction,
  validateAmount,
  validateBalance,
  applyTransaction,
  calculateBalance as calcBalance,
} from '@/lib/utils/financialUtils';

export interface AccountContextType {
  // State
  accountId: string;
  balance: number;
  transactions: FinancialTransaction[];
  isLoading: boolean;

  // Actions
  addTransaction: (
    type: FinancialTransaction['type'],
    amount: number,
    description: string,
    status?: 'completed' | 'pending'
  ) => Promise<FinancialTransaction | null>;

  deductBalance: (amount: number, description: string, type: 'withdrawal' | 'transfer' | 'bill_payment') => Promise<boolean>;

  addBalance: (amount: number, description: string, type: 'deposit' | 'interest') => Promise<boolean>;

  refreshTransactions: () => Promise<void>;

  updateTransactionStatus: (transactionId: string, status: 'completed' | 'pending' | 'failed') => void;

  // Approval actions
  approveTransaction: (transactionId: string, managerName: string) => Promise<boolean>;

  rejectTransaction: (transactionId: string, managerName: string) => Promise<boolean>;

  // Activity logging
  activityLog: Array<{
    id: string;
    action: string;
    itemType: string;
    amount?: number;
    managerName: string;
    timestamp: string;
  }>;

  addActivityLog: (action: string, itemType: string, amount: number | undefined, managerName: string) => void;
}

const AccountContext = createContext<AccountContextType | null>(null);

const STORAGE_KEY = 'oakmerepay-account-data';

export function AccountProvider({ children, accountId = 'acct-001' }: { children: ReactNode; accountId?: string }) {
  const [balance, setBalance] = useState<number>(5000);
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activityLog, setActivityLog] = useState<Array<{
    id: string;
    action: string;
    itemType: string;
    amount?: number;
    managerName: string;
    timestamp: string;
  }>>([]);

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setBalance(data.balance);
        setTransactions(data.transactions);
        setActivityLog(data.activityLog || []);
      }
    } catch {
      console.warn('Unable to load saved account data from localStorage');
    }
    setIsLoading(false);
  }, []);

  // Save to localStorage whenever balance or transactions change
  useEffect(() => {
    if (typeof window === 'undefined' || isLoading) return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ balance, transactions, activityLog, accountId }));
    } catch {
      console.warn('Unable to save account data to localStorage');
    }
  }, [balance, transactions, isLoading, accountId, activityLog]);

  const addTransaction = useCallback(
    async (
      type: FinancialTransaction['type'],
      amount: number,
      description: string,
      status: 'completed' | 'pending' = 'completed'
    ): Promise<FinancialTransaction | null> => {
      // Validate amount
      const validation = validateAmount(amount);
      if (!validation.isValid) {
        console.error('Transaction validation failed:', validation.error);
        return null;
      }

      // For debit transactions, validate balance
      if (['withdrawal', 'transfer', 'bill_payment', 'fee'].includes(type)) {
        const balanceValidation = validateBalance(balance, amount);
        if (!balanceValidation.isValid) {
          console.error('Insufficient balance:', balanceValidation.error);
          return null;
        }
      }

      // Create transaction
      const transaction = createTransaction(accountId, type, amount, description, status);

      // Update state
      setTransactions((prev) => [transaction, ...prev]);

      // Update balance if completed
      if (status === 'completed') {
        setBalance((prev) => applyTransaction(prev, transaction));
      }

      return transaction;
    },
    [balance, accountId]
  );

  const deductBalance = useCallback(
    async (
      amount: number,
      description: string,
      type: 'withdrawal' | 'transfer' | 'bill_payment'
    ): Promise<boolean> => {
      const result = await addTransaction(type, amount, description, 'completed');
      return result !== null;
    },
    [addTransaction]
  );

  const addBalance = useCallback(
    async (
      amount: number,
      description: string,
      type: 'deposit' | 'interest'
    ): Promise<boolean> => {
      const result = await addTransaction(type, amount, description, 'completed');
      return result !== null;
    },
    [addTransaction]
  );

  const refreshTransactions = useCallback(async () => {
    // This could fetch from Supabase in the future
    // For now, it's a no-op since we manage it locally
    setIsLoading(false);
  }, []);

  const updateTransactionStatus = useCallback(
    (transactionId: string, status: 'completed' | 'pending' | 'failed') => {
      setTransactions((prev) =>
        prev.map((t) => (t.id === transactionId ? { ...t, status } : t))
      );
    },
    []
  );

  const approveTransaction = useCallback(
    async (transactionId: string, managerName: string): Promise<boolean> => {
      try {
        setTransactions((prev) =>
          prev.map((t) => {
            if (t.id === transactionId) {
              // Apply balance change for debit/credit transactions
              if (t.status !== 'completed' && (t.direction === 'debit' || t.direction === 'credit')) {
                setBalance((b) => applyTransaction(b, t));
              }
              return {
                ...t,
                status: 'approved',
                reviewedBy: managerName,
                reviewedAt: new Date().toISOString(),
              };
            }
            return t;
          })
        );
        addActivityLog('approved', 'transaction', undefined, managerName);
        return true;
      } catch (error) {
        console.error('Error approving transaction:', error);
        return false;
      }
    },
    []
  );

  const rejectTransaction = useCallback(
    async (transactionId: string, managerName: string): Promise<boolean> => {
      try {
        setTransactions((prev) =>
          prev.map((t) =>
            t.id === transactionId
              ? {
                  ...t,
                  status: 'rejected',
                  reviewedBy: managerName,
                  reviewedAt: new Date().toISOString(),
                }
              : t
          )
        );
        addActivityLog('rejected', 'transaction', undefined, managerName);
        return true;
      } catch (error) {
        console.error('Error rejecting transaction:', error);
        return false;
      }
    },
    []
  );

  const addActivityLog = useCallback(
    (action: string, itemType: string, amount: number | undefined, managerName: string) => {
      const logEntry = {
        id: `log-${Date.now()}`,
        action,
        itemType,
        amount,
        managerName,
        timestamp: new Date().toISOString(),
      };
      setActivityLog((prev) => [logEntry, ...prev]);
    },
    []
  );

  const value: AccountContextType = {
    accountId,
    balance,
    transactions,
    isLoading,
    addTransaction,
    deductBalance,
    addBalance,
    refreshTransactions,
    updateTransactionStatus,
    approveTransaction,
    rejectTransaction,
    activityLog,
    addActivityLog,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextType {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within AccountProvider');
  }
  return context;
}
